import { ABILITY_DEFS, ARCHETYPE_ABILITY } from '../data/abilities.js';
import { CUT_LINK_TARGET_RANGE_SQ } from '../data/cutLink.js';
import type { PlayerView } from '../matchHost/MatchHost.js';
import { makeAbilityCommand } from '../simulation/commands/AbilityCommand.js';
import { makePickDoctrineCommand } from '../simulation/commands/PickDoctrineCommand.js';
import type { Core } from '../simulation/entities/Core.js';
import type { Squad, SquadArchetype } from '../simulation/entities/Squad.js';
import type { PlayerId, Tick } from '../simulation/ids.js';
import { otherPlayer, tickOf } from '../simulation/ids.js';

import type { Intent, TacticalBotPlayerOptions } from './TacticalBotPlayer.js';
import { TacticalBotPlayer } from './TacticalBotPlayer.js';
import type { DesiredOrder, RelayBias } from './botShared.js';
import {
  adaptiveCompositionQuota,
  pickSupportRelayChannelPoint,
  pickSupportRelayToSnipe,
  sqDist,
  squadValue,
} from './botShared.js';

// Commander-tier bot for V1.5. Subclass of TacticalBotPlayer (Rule 6 — same
// CommandSource shape, same Command surface). Inherits Tactical's snapshot
// pipeline / produce / build / order plumbing; overrides only the read hooks
// that change at the top tier:
//
//   - Reaction delay 175 ms (vs Tactical's 300 ms, Recruit's 800 ms).
//     Behavioral nerf only — no income mod, no perfect-information cheats
//     (docs/07_BOT_AI.md §Difficulties). V1.7 (3.16) tightened the Commander
//     bar from 250 → 175 ms after the playtest finding that the top tier
//     felt too easy; the gap to Tactical (now 300 ms) stays at ~125 ms so
//     the two tiers remain distinguishable on reaction alone.
//   - Predicts pushes from enemy production state (docs/07_BOT_AI.md §Commander):
//     if the visible enemy Production Hub queue has a Shield + Assault head AND
//     our army value is below the enemy's, switch to defensive posture one tick
//     earlier than Tactical's threshold. Triggers `defend` even at 65% Core HP
//     when the read says a real push is queued.
//   - Doctrine pick read on the matchup, not on what we've already built:
//       · saboteur — own Recon alive AND enemy ≥ 1 Shield (5% maxHp on apply
//         chips Shield-anchored frontlines).
//       · bulwark  — bot already has a Hub OR Turret, OR enemy army value at
//         pick time > 1.25× own (Bulwark + comeback levers compound).
//       · vanguard — otherwise.
//     Deterministic — no Math.random (Rule 8).
//   - Mid-match adaptation via Underdog Surge:
//       · own surge active     → bias capture mode (favor neutral / enemy
//         relays; cheaper squads compound with the +25% income).
//       · enemy surge active   → push HARDER on the leader's relays before the
//         gap shrinks.
//   - Mark Target on the highest-maxHp enemy (Saboteur-aware). The 5% maxHp
//     on apply scales with maxHp, so painting a Shield costs the most pool.
//
// Difficulty knob: only reaction delay + slightly tighter triggers. No income
// mod, no perfect-information cheats — every read uses the delayed snapshot
// pipeline already in TacticalBotPlayer (07_BOT_AI.md §Difficulties).

const COMMANDER_REACTION_DELAY_MS = 175;

// Commander's tightened defend trigger when a real push is queued (vs Tactical's
// 50% Core HP fixed threshold). One tick earlier = Core HP < 65% and a confirmed
// enemy push composition with a value disadvantage.
const COMMANDER_PREDICT_DEFEND_HP_RATIO = 0.65;

// Enemy "army ahead" gate for the bulwark pick: enemyValue > BULWARK_AHEAD_MULT × ownValue.
const BULWARK_AHEAD_MULT = 1.25;

export class CommanderBotPlayer extends TacticalBotPlayer {
  constructor(opts: TacticalBotPlayerOptions) {
    super({
      playerId: opts.playerId,
      reactionDelayMs: opts.reactionDelayMs ?? COMMANDER_REACTION_DELAY_MS,
    });
  }

  // Overridden doctrine pick. Same PickDoctrineCommand a human would emit
  // (Rule 6); deterministic (Rule 8).
  protected override maybeEmitDoctrinePick(snap: PlayerView, liveTick: Tick): void {
    if (this.emit === null) return;
    if (!snap.doctrinePickWindowOpen) return;
    if (snap.players[this.playerId].doctrineId !== null) return;
    const pick = pickDoctrineCommander(snap, this.playerId);
    this.emit(
      makePickDoctrineCommand({
        issuedBy: this.playerId,
        tick: tickOf(liveTick + 1),
        doctrineId: pick,
      }),
    );
  }

  // Overridden intent read. Falls through to Tactical's standard rule first; if
  // that says capture/push, we layer Commander-specific reads on top:
  //   1. Predict-push: confirmed Shield+Assault hub queue + value disadvantage
  //      with Core HP under COMMANDER_PREDICT_DEFEND_HP_RATIO → flip to defend.
  //   2. Underdog Surge active for us → prefer capture (cheaper squads + more
  //      income from SURGE = faster recapture). We don't downgrade defend to
  //      capture because defend is a survival read; surge bias is for the
  //      "should we push or capture?" branch.
  //   3. Underdog Surge active for the enemy → push HARDER. If Tactical says
  //      capture, escalate to push when we're at parity-or-better on value.
  protected override chooseIntent(
    snap: PlayerView,
    ownSquads: Squad[],
    enemySquads: Squad[],
    ownCore: Core | null,
  ): Intent {
    const base = super.chooseIntent(snap, ownSquads, enemySquads, ownCore);
    if (base === 'defend') return 'defend';

    if (
      ownCore !== null
      && ownCore.maxHp > 0
      && ownCore.hp / ownCore.maxHp < COMMANDER_PREDICT_DEFEND_HP_RATIO
      && enemyHasQueuedPush(snap, this.playerId)
      && armyValueDisadvantage(ownSquads, enemySquads)
    ) {
      return 'defend';
    }

    const ownSurge = snap.underdogSurgeActive[this.playerId] === true;
    const enemyId = otherPlayer(this.playerId);
    const enemySurge = snap.underdogSurgeActive[enemyId] === true;
    if (ownSurge) return 'capture';
    if (enemySurge && base === 'capture') {
      const ownValue = ownSquads.reduce((s, sq) => s + squadValue(sq), 0);
      const enemyValue = enemySquads.reduce((s, sq) => s + squadValue(sq), 0);
      if (ownValue >= enemyValue && ownSquads.length >= 2 && ownCore !== null) return 'push';
    }
    return base;
  }

  // V1.5+ "raid the wounded forward position" instinct
  // (docs/02_GAME_DESIGN.md §14 success state: "If I flip this relay, his
  // forward pressure collapses"). When an enemy mini-turret has gone offline
  // (network-disconnected per ForwardTurretNetworkSystem), Commander redirects
  // its non-Engineer squads to attack-move onto it instead of routing to a
  // relay. Engineers stay on capture duty (they're the slow tempo unit and
  // shouldn't be diverted from relay touches). The wounded turret is in
  // CommanderRaidRangeSquared of the squad — far enough that a forward
  // diversion is plausible, tight enough that the squad doesn't chase a
  // turret across the map and abandon a closer relay opportunity.
  // Deterministic — no RNG (Rule 8); pure state read.
  //
  // V1.5+ overhaul: layered on top of this, Commander's Recon now snipes the
  // *support relay* anchoring an enemy turret cluster when we're army-value
  // disadvantaged — the "attack the network, not the army" instinct from
  // 07_BOT_AI.md §Commander. Instead of walking the Recon at a far relay, we
  // walk it at the chain anchor and the secondary-ability E-loop emits a Cut
  // Link cast that dims the whole cluster. The squad still benefits from
  // standard ability gates (range, channel exposure) — we only set the
  // routing target.
  protected override captureOrderFor(
    squad: Squad,
    snap: PlayerView,
    ownCore: Core | null,
    enemyCore: Core | null,
    enemySquads: Squad[],
  ): DesiredOrder | null {
    // Anti-deathball Recon redirect: if we're army-disadvantaged AND the
    // enemy has a forward turret cluster, walk the Recon at the support
    // relay so the next E-ability tick can land a Cut Link. The standard
    // pickRelayTarget would route the Recon around the cluster; we override
    // because the cut is the comeback play (07_BOT_AI.md §Commander
    // "attacks the network, not the army").
    if (squad.archetype === 'recon') {
      const ownSquads: Squad[] = Object.values(snap.squads).filter(
        (s) => s.ownerId === this.playerId,
      );
      const ownValue = ownSquads.reduce((s, sq) => s + squadValue(sq), 0);
      const enemyValue = enemySquads.reduce((s, sq) => s + squadValue(sq), 0);
      const disadvantaged = enemyValue > ownValue * 1.15;
      if (disadvantaged) {
        // pickSupportRelayChannelPoint returns a position 200 px from the
        // support relay on the far side from the turret cluster — Recon
        // ends the walk inside Cut Link range AND with the relay between
        // it and the densest turret fire. Walking the relay CENTER (as
        // the original draft did) marched the Recon straight into the
        // cluster and the 3 s channel died to incoming damage.
        const channelPoint = pickSupportRelayChannelPoint(snap, this.playerId);
        if (channelPoint !== null) {
          // Move (not attack-move) so the Recon doesn't engage the cluster
          // en route — its job is to land in Cut Link range and channel,
          // not to trade against the turrets it's about to dim. The
          // standard ability dispatcher (tryCutLink) will fire on the
          // next tick once we're inside CUT_LINK_TARGET_RANGE_SQ.
          return { kind: 'move', target: channelPoint.pos };
        }
      }
    }

    // Note: the original draft had an Assault/Shield "refuse losing local
    // fights" gate here, but the playtest grid showed it cost Commander too
    // much initiative (0/6 vs Tactical at 1.6× threshold). The decision was
    // moved to orderForSquad via overrideLosingFightRedirect so we can
    // intervene BEFORE focus-fire fires (the previous placement after
    // focus-fire was a no-op for these archetypes).

    if (squad.archetype !== 'recon' && squad.archetype !== 'engineer') {
      const wounded = pickWoundedEnemyMiniTurret(snap, squad, this.playerId);
      if (wounded !== null) {
        return { kind: 'attack_move', target: wounded };
      }
    }
    return super.captureOrderFor(squad, snap, ownCore, enemyCore, enemySquads);
  }

  // V1.5+ overhaul — composition override. Commander tightens Tactical's
  // adaptive read with two matchup-specific instincts:
  //   1. Under enemy push pressure (enemy has a Hub queueing Shield+Assault)
  //      AND we have no Shield yet → bump the Shield quota so the next
  //      production landfall is a defender.
  //   2. Under our own surge → bump the Assault quota by 1 (cheaper squads
  //      compound, and we want raw DPS to convert the income spike into
  //      board presence).
  // Falls through to Tactical's base read when neither instinct fires.
  protected override composeQuotaFor(
    snap: PlayerView,
    enemySquads: Squad[],
    squadCap: number,
  ): Record<SquadArchetype, number> {
    const base = adaptiveCompositionQuota(enemySquads, squadCap);
    // Detect enemy push pressure.
    if (enemyHasQueuedPush(snap, this.playerId)) {
      if (base.shield < 2) {
        base.shield += 1;
        if (base.assault > 1) base.assault -= 1;
        else if (base.engineer > 0) base.engineer -= 1;
      }
    }
    // Surge instinct: under our own surge, prefer DPS.
    if (snap.underdogSurgeActive[this.playerId] === true) {
      if (base.assault < 3) {
        base.assault += 1;
        if (base.shield > 1) base.shield -= 1;
        else if (base.engineer > 0) base.engineer -= 1;
        else if (base.recon > 0) base.recon -= 1;
      }
    }
    // Clamp to cap by trimming highest-count last (preserves Assault spine).
    let sum = base.recon + base.engineer + base.shield + base.assault;
    const trimOrder: SquadArchetype[] = ['recon', 'engineer', 'shield', 'assault'];
    let i = 0;
    while (sum > squadCap && i < trimOrder.length) {
      const a = trimOrder[i]!;
      if (base[a] > 0) {
        base[a] -= 1;
        sum -= 1;
      } else {
        i += 1;
      }
    }
    return base;
  }

  // Surge-aware capture-mode bias.
  //
  // V1.5+ Slice 8 — Signal Storm Endgame takes precedence. When the storm
  // phase is active, the gate becomes "enemy-surge OR storm" → always
  // 'pressure_leader'. Critical: the storm gate is OUTERMOST so a regulation
  // surge that's still active when the storm arrives doesn't accidentally
  // push the bot back into 'capture' (own-surge) at 7:00 — pressing the
  // leader is unambiguously correct in the final 60s regardless of who
  // owns the surge.
  protected override relayBias(snap: PlayerView): RelayBias {
    if (snap.phase === 'signal_storm') return 'pressure_leader';
    const ownSurge = snap.underdogSurgeActive[this.playerId] === true;
    if (ownSurge) return 'capture';
    const enemyId = otherPlayer(this.playerId);
    const enemySurge = snap.underdogSurgeActive[enemyId] === true;
    if (enemySurge) return 'pressure_leader';
    return 'default';
  }

  // V1.5+ overhaul — Commander-tier Cut Link. Tactical's Cut Link prefers an
  // in-range, enemy-connected relay; Commander tightens that with a strict
  // anchor-count priority. If a support relay (one anchoring ≥1 online enemy
  // mini-turret) is in range, Commander cuts THAT relay — dimming a cluster
  // is the comeback play. Falls back to Tactical's standard read when no
  // support relay is in Cut Link range.
  protected override tryCutLink(recon: Squad, snap: PlayerView, liveTick: Tick): void {
    if (this.emit === null) return;
    const supportRelay = pickSupportRelayToSnipe(snap, this.playerId);
    if (supportRelay !== null && sqDist(recon.pos, { x: supportRelay.x, y: supportRelay.y }) <= CUT_LINK_TARGET_RANGE_SQ) {
      this.emit(
        makeAbilityCommand({
          issuedBy: this.playerId,
          tick: tickOf(liveTick + 1),
          squadId: recon.id,
          hotkey: 'E',
          target: { kind: 'control_node', id: supportRelay.id },
        }),
      );
      return;
    }
    super.tryCutLink(recon, snap, liveTick);
  }

  // V1.5+ overhaul — same retreat threshold as Tactical for non-Recon
  // squads (a-too-cautious Commander loses tempo and is dominated by
  // Tactical's straighter pressure — measured at 0/6 in the playtest
  // grid when set to 0.35). Recon retreats slightly earlier (0.50 vs
  // Tactical's 0.45) because Commander's anti-deathball read needs the
  // Recon alive for the Cut Link play; a dead Recon = no comeback.
  protected override retreatThresholdFor(squad: Squad): number {
    if (squad.archetype === 'recon') return 0.50;
    return super.retreatThresholdFor(squad);
  }

  // Mark Target on the highest-maxHp enemy in range (Saboteur-aware). Tactical
  // picks highest current HP; under Saboteur, the 5% maxHp on apply scales with
  // maxHp — Shields are the highest-value paint by a wide margin. Still gated
  // on an Assault ally being in skirmish range so the +10% damage multiplier
  // bands the 8 s mark window.
  protected override tryMarkTarget(
    recon: Squad,
    ownSquads: Squad[],
    enemySquads: Squad[],
    liveTick: Tick,
  ): void {
    if (this.emit === null) return;
    const def = ABILITY_DEFS[ARCHETYPE_ABILITY.recon];
    const range = def.markRange ?? 600;
    const rangeSq = range * range;
    // Pick highest maxHp; ties broken by current HP (so we don't waste paint on
    // a near-dead Shield when a fresh one is in range).
    let bestTarget: Squad | null = null;
    let bestMaxHp = -1;
    let bestHp = -1;
    for (const enemy of enemySquads) {
      if (sqDist(enemy.pos, recon.pos) > rangeSq) continue;
      if (
        enemy.maxHp > bestMaxHp
        || (enemy.maxHp === bestMaxHp && enemy.hp > bestHp)
      ) {
        bestMaxHp = enemy.maxHp;
        bestHp = enemy.hp;
        bestTarget = enemy;
      }
    }
    if (bestTarget === null) return;
    // Commander still wants the +10% damage multiplier to bank, so it gates on
    // an Assault ally being within skirmish range of the target — same as
    // Tactical, but the target is selected by maxHp.
    const skirmishSq = 350 * 350;
    let assaultClose = false;
    for (const ally of ownSquads) {
      if (ally.archetype !== 'assault') continue;
      if (sqDist(ally.pos, bestTarget.pos) <= skirmishSq) {
        assaultClose = true;
        break;
      }
    }
    if (!assaultClose) return;
    this.emit(
      makeAbilityCommand({
        issuedBy: this.playerId,
        tick: tickOf(liveTick + 1),
        squadId: recon.id,
        target: { kind: 'squad', id: bestTarget.id },
      }),
    );
  }
}

// Doctrine pick rule for Commander, exported for tests so we can assert without
// reaching into the bot's emit pipe.
export function pickDoctrineCommander(
  snap: PlayerView,
  playerId: PlayerId,
): 'vanguard' | 'saboteur' | 'bulwark' {
  const enemyId = otherPlayer(playerId);
  let ownReconAlive = false;
  for (const sq of Object.values(snap.squads)) {
    if (sq.ownerId !== playerId) continue;
    if (sq.archetype === 'recon') {
      ownReconAlive = true;
      break;
    }
  }
  let enemyHasShield = false;
  for (const sq of Object.values(snap.squads)) {
    if (sq.ownerId !== enemyId) continue;
    if (sq.archetype === 'shield') {
      enemyHasShield = true;
      break;
    }
  }
  if (ownReconAlive && enemyHasShield) return 'saboteur';

  let hasStructure = false;
  for (const b of Object.values(snap.buildings)) {
    if (b.ownerId !== playerId) continue;
    if (b.kind === 'production_hub' || b.kind === 'turret') {
      hasStructure = true;
      break;
    }
  }
  let ownValue = 0;
  let enemyValue = 0;
  for (const sq of Object.values(snap.squads)) {
    const v = squadValue(sq);
    if (sq.ownerId === playerId) ownValue += v;
    else if (sq.ownerId === enemyId) enemyValue += v;
  }
  // Strict ahead — equality falls through to vanguard so a fresh-match snapshot
  // (both sides at one Recon = parity) doesn't flip into Bulwark.
  const enemyAhead = ownValue > 0 ? enemyValue > BULWARK_AHEAD_MULT * ownValue : enemyValue > 0;
  if (hasStructure || enemyAhead) return 'bulwark';
  return 'vanguard';
}

// V1.5+ wounded-mini-turret pursuit radius. Tight enough that the diversion
// stays "this turret is on the way" (≈ the width of the central battlefield)
// rather than a cross-map chase. Squared once and held in a const so the
// hot path (per-squad, per-AI-tick) doesn't reallocate.
const COMMANDER_RAID_RANGE_PX = 1100;
const COMMANDER_RAID_RANGE_SQ = COMMANDER_RAID_RANGE_PX * COMMANDER_RAID_RANGE_PX;

function pickWoundedEnemyMiniTurret(
  snap: PlayerView,
  squad: Squad,
  ownerId: PlayerId,
): { x: number; y: number } | null {
  let best: { x: number; y: number } | null = null;
  let bestSq = Infinity;
  for (const b of Object.values(snap.buildings)) {
    if (b.kind !== 'mini_turret') continue;
    if (b.ownerId === ownerId) continue;
    if (b.isOnline) continue;
    const dSq = sqDist(b.pos, squad.pos);
    if (dSq > COMMANDER_RAID_RANGE_SQ) continue;
    if (dSq < bestSq) {
      bestSq = dSq;
      best = { x: b.pos.x, y: b.pos.y };
    }
  }
  return best;
}

// True iff the visible enemy Production Hub has both a Shield and an Assault
// head among the next two queued archetypes. The "head" is the queue's
// front-most slots — that's the next push composition coming online.
function enemyHasQueuedPush(snap: PlayerView, playerId: PlayerId): boolean {
  const enemyId = otherPlayer(playerId);
  for (const b of Object.values(snap.buildings)) {
    if (b.ownerId !== enemyId) continue;
    if (b.kind !== 'production_hub') continue;
    const queue = b.produces.queue;
    if (queue.length === 0) continue;
    const head = queue.slice(0, 2);
    const hasShield = head.includes('shield');
    const hasAssault = head.includes('assault');
    if (hasShield && hasAssault) return true;
  }
  return false;
}

function armyValueDisadvantage(ownSquads: Squad[], enemySquads: Squad[]): boolean {
  let ownValue = 0;
  let enemyValue = 0;
  for (const sq of ownSquads) ownValue += squadValue(sq);
  for (const sq of enemySquads) enemyValue += squadValue(sq);
  return enemyValue > ownValue;
}
